import { motion } from 'framer-motion';
import { Coins, Hash, Layers, TrendingUp } from 'lucide-react';
import { fmtTokens, fmtUSD, projectMonthly } from '../lib/estimate.js';

export function EstimatePanel({ estimate, runsPerDay = 50 }) {
  const monthly = projectMonthly(estimate.total, runsPerDay);
  const stats = [
    { icon: Hash, label: 'input tokens', value: fmtTokens(estimate.inputTokens), tone: 'cyan' },
    { icon: Layers, label: 'output tokens', value: fmtTokens(estimate.outputTokens), tone: 'cyan' },
    { icon: Coins, label: 'cost / run', value: fmtUSD(estimate.total), tone: 'orange' },
    { icon: TrendingUp, label: `monthly @ ${runsPerDay}/day`, value: fmtUSD(monthly), tone: 'orange' },
  ];

  return (
    <motion.section
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.55, delay: 0.15 }}
      className="panel-strong p-5"
    >
      <div className="mb-4 flex items-center justify-between">
        <div className="section-title">
          <TrendingUp className="h-4 w-4 text-accent-orange" />
          Live estimate
        </div>
        <span className="font-mono text-[10px] text-ink-dim">char/token ≈ 3.8 · output budget ≈ 70%</span>
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {stats.map((s) => (
          <StatBox key={s.label} {...s} />
        ))}
      </div>

      <div className="my-4 divider" />

      <div className="space-y-2">
        {estimate.perAgent.map((a, i) => (
          <AgentLine key={`${a.key}-${i}`} agent={a} max={Math.max(estimate.total, 0.0001)} />
        ))}
        {estimate.judge && (
          <>
            <div className="my-2 flex items-center gap-2 text-[10px] uppercase tracking-[0.16em] text-ink-dim">
              <span className="h-px flex-1 bg-border-subtle/60" />
              judge
              <span className="h-px flex-1 bg-border-subtle/60" />
            </div>
            <AgentLine agent={estimate.judge} max={Math.max(estimate.total, 0.0001)} judge />
          </>
        )}
      </div>
    </motion.section>
  );
}

function StatBox({ icon: Icon, label, value, tone }) {
  const toneCls = tone === 'orange' ? 'text-accent-orange-soft' : 'text-accent-cyan-light';
  const ringCls = tone === 'orange' ? 'ring-accent-orange/30' : 'ring-accent-cyan/30';
  return (
    <motion.div
      whileHover={{ y: -2 }}
      className={`relative overflow-hidden rounded-xl border border-border-subtle bg-bg-deep/50 p-3 ring-1 ${ringCls}`}
    >
      <div className="flex items-center justify-between">
        <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-ink-dim">{label}</span>
        <Icon className={`h-3.5 w-3.5 ${toneCls}`} />
      </div>
      <div className={`mt-1 font-display text-2xl font-semibold tabular-nums ${toneCls}`}>{value}</div>
    </motion.div>
  );
}

function AgentLine({ agent, max, judge }) {
  const pct = max > 0 ? Math.min(100, (agent.total / max) * 100) : 0;
  return (
    <div className="group">
      <div className="mb-1 flex items-center justify-between text-xs">
        <div className="flex items-center gap-2">
          <span className="h-2 w-2 rounded-full" style={{ background: agent.color }} />
          <span className={`font-medium ${judge ? 'text-accent-orange-soft' : 'text-ink-primary'}`}>{agent.label}</span>
          <span className="font-mono text-[10px] text-ink-dim">
            {fmtTokens(agent.inputTokens)} → {fmtTokens(agent.outputTokens)}
          </span>
        </div>
        <span className="font-mono text-xs tabular-nums text-ink-muted">{fmtUSD(agent.total)}</span>
      </div>
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-bg-deep">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${pct}%` }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="h-full rounded-full"
          style={{ background: `linear-gradient(90deg, ${agent.color}aa, ${agent.color})` }}
        />
      </div>
    </div>
  );
}
